// Backup-audio upload — the ONE implementation of
// `POST /sessions/{id}/backup-audios/` (ruling 22, ARCFE-1127).
//
// DOMAIN RULING: the route is a SESSION resource, so its door is
// `@heidi/session/data`. Judged by the resource-owner rule: the capture kit is
// engine MECHANISM, not a resource owner, and the hub twin has been tagged
// `@domain session` since long before this carve.
//
// Two app paths posted this route with two hand-written bodies — the hub's
// `uploadBackupAudio` (object arg, percent progress, a `logger.debug` on every
// progress event) and the capture provider's `uploadBackup` (positional arg,
// fraction progress, `AbortSignal`). Both are now thin app-side adapters over
// this fn, which is the union of what each needed:
//
//   * `signal` is spread conditionally, so the hub path still sends a config
//     object with no `signal` key at all;
//   * `onUploadProgress` reports a FRACTION, because a fraction is the thing
//     the transport actually knows — percent rounding is a presentation choice
//     and stays with the caller that makes it;
//   * `onProgressEvent` hands the raw axios event back for the hub path's
//     `logger.debug`, which keeps the log app-side (this zone is headless) and
//     lets that path compute its percent from `loaded`/`total` exactly as it
//     always has, rather than re-deriving it from a rounded fraction.
//
// `deserializeBlob` is deliberately NOT here: it is capture's, and
// `@heidi/session` holds no session -> capture edge (the D2 rulings across
// `src/data/mutation/**` say so repeatedly). The package takes a plain `Blob`
// and the provider-path adapter does the deserialization.

import { getScribeHttpClients } from '@heidi/api/client';
import { type AxiosProgressEvent } from 'axios';

export interface UploadBackupAudioInput {
  sessionId: string;
  /** The recorded audio. Callers holding a serialized blob deserialize first. */
  file: Blob;
  /** Appended as `extension` when non-empty — both old paths gated on truthiness. */
  extension?: string;
  /** Appended as `start_time`/`end_time`, and only when BOTH are present. */
  startTime?: number | null;
  endTime?: number | null;
  /** Progress as a fraction in [0, 1]. Fires only when the transfer reports a total. */
  onUploadProgress?: (fraction: number) => void;
  /** Every raw progress event, including ones carrying no total. */
  onProgressEvent?: (event: AxiosProgressEvent) => void;
  signal?: AbortSignal;
}

/** `POST /sessions/:id/backup-audios/` (v2) — multipart upload of a session's backup recording. */
export const uploadBackupAudio = ({
  sessionId,
  file,
  extension,
  startTime,
  endTime,
  onUploadProgress,
  onProgressEvent,
  signal,
}: UploadBackupAudioInput) => {
  const formData = new FormData();
  formData.append('file', file);
  if (startTime && endTime) {
    formData.append('start_time', startTime.toString());
    formData.append('end_time', endTime.toString());
  }
  if (extension) {
    formData.append('extension', extension);
  }

  return getScribeHttpClients().scribeHttpV2.post<string>(
    `/sessions/${sessionId}/backup-audios/`,
    formData,
    {
      ...(signal ? { signal } : {}),
      onUploadProgress: (event) => {
        onProgressEvent?.(event);
        if (!event.total) return;
        onUploadProgress?.(event.loaded / event.total);
      },
    }
  );
};
